/**
 * MapLibre contour-interval filter helpers (Phase 1b terrain tool card).
 *
 * Contour PMTiles from the converter carry an `elev` property at the native
 * interval (~10 m). The tool-card slider coarsens display via an elevation
 * modulo filter without re-running gdal_contour.
 */

export type ContourIntervalFilter = ['==', ['%', ['get', 'elev'], number], 0];

/** Build a MapLibre filter for the chosen contour interval, or `null` to show all. */
export function buildContourIntervalFilter(intervalM: number): ContourIntervalFilter | null {
  if (!Number.isFinite(intervalM) || intervalM <= 1) return null;
  return ['==', ['%', ['get', 'elev'], intervalM], 0];
}

export interface MapLibreFilterTarget {
  getLayer(id: string): unknown;
  setFilter(id: string, filter: ContourIntervalFilter | null): void;
}

/**
 * Apply the interval filter to every MapLibre sub-layer for a contour vector
 * layer. Returns `true` when at least one sub-layer received the filter.
 */
export function applyContourIntervalFilter(
  map: MapLibreFilterTarget | null | undefined,
  contourLayerId: string,
  intervalM: number,
): boolean {
  if (!map) return false;
  const filter = buildContourIntervalFilter(intervalM);
  let applied = false;
  for (const suffix of ['-line', '-fill', '']) {
    const mlLayerId = `layer-${contourLayerId}${suffix}`;
    try {
      if (map.getLayer(mlLayerId)) {
        map.setFilter(mlLayerId, filter);
        applied = true;
      }
    } catch {
      /* layer may not be mounted yet */
    }
  }
  return applied;
}

/**
 * Re-apply the contour filter until MapLibre sub-layers exist or attempts
 * exhaust. Covers async PMTiles mount after the tool card toggles contours on.
 */
export function scheduleContourIntervalFilter(
  map: MapLibreFilterTarget | null | undefined,
  contourLayerId: string,
  intervalM: number,
  maxAttempts = 30,
): void {
  if (!map) return;
  let attempts = 0;
  const tick = (): void => {
    if (applyContourIntervalFilter(map, contourLayerId, intervalM)) return;
    attempts += 1;
    if (attempts < maxAttempts) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}
